import { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Navigate, BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ConfigProvider, Spin } from 'antd';
import { StyleProvider, legacyLogicalPropertiesTransformer } from '@ant-design/cssinjs';
import { hasAuthToken } from '@/utils/auth';
import { initClarity } from '@/utils/clarity';
import '@/less/global.less';

const Home = lazy(() => import("./pages/home"));
const Login = lazy(() => import("./pages/login"));
const PayResult = lazy(() => import("./pages/payResult"));

window.env = "prod";
window.apiPrefix = "https://api.dalipen.com";
window.initRoute = "/home";
window.country = "US";
window.googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || window.googleClientId || "";
initClarity();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const RequireAuth = ({ children }: { children: JSX.Element }) => {
  const location = useLocation();
  const loginTarget = location.search ? `/login${location.search}` : '/login';
  return hasAuthToken() ? children : <Navigate to={loginTarget} replace />;
};

(async () => {
  try {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 2500);
    let result: any = await fetch(window.apiPrefix + "/whereami?t=" + new Date().getTime(), {
      signal: controller.signal
    });
    window.clearTimeout(timer);
    result = await (result).json();
    if (result.code === 0) {
      window.country = result.data;
    }
  } catch (error) {
    window.country = window.country || "US";
  }

  let antdLang;  // antd 依赖的语言包
  let dayjsLang;  // dayjs 依赖的语言包

  switch (window.country) {
    case "CN":
      window.lang = require('@/assets/locale/zh').default;
      antdLang = require('antd/es/locale/zh_CN').default;
      dayjsLang = require('dayjs/locale/zh-cn').default;
      break;
    case "TW":
      window.lang = require('@/assets/locale/zh-Hant').default;
      antdLang = require('antd/es/locale/zh_TW').default;
      dayjsLang = require('dayjs/locale/zh-tw').default;
      break;
    case "IN":
      window.lang = require('@/assets/locale/hi').default;
      antdLang = require('antd/es/locale/en_US').default; // antd doesn't have Hindi locale
      dayjsLang = require('dayjs/locale/hi').default;
      break;
    case "JP":
      window.lang = require('@/assets/locale/ja').default;
      antdLang = require('antd/es/locale/ja_JP').default;
      dayjsLang = require('dayjs/locale/ja').default;
      break;
    case "KR":
      window.lang = require('@/assets/locale/ko').default;
      antdLang = require('antd/es/locale/ko_KR').default;
      dayjsLang = require('dayjs/locale/ko').default;
      break;
    default:
      window.lang = require('@/assets/locale/en').default;
      antdLang = require('antd/es/locale/en_US').default;
      dayjsLang = require('dayjs/locale/en').default;
      break;
  }

  root.render(
    // <React.StrictMode>
    <ConfigProvider locale={antdLang}>
      <StyleProvider hashPriority="high" transformers={[legacyLogicalPropertiesTransformer]}>
        <BrowserRouter>
          <Suspense fallback={<div className="route-loading"><Spin /></div>}>
            <Routes>
              <Route path="/" element={<Navigate to={hasAuthToken() ? "/home" : "/login"} replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/home" element={<RequireAuth><Home /></RequireAuth>} />
              <Route path="/pay_success.html" element={<PayResult result="success" />} />
              <Route path="/pay_cancel.html" element={<PayResult result="fail" />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </StyleProvider>
    </ConfigProvider >
    // </React.StrictMode>
  );
  document.documentElement.classList.add('app-ready');
})();
